import { forwardRef, useState } from 'react'
import cx from 'classnames'
import Styles from './LastMessageActionsV2.module.scss'
import { LastMessageActionsProps } from './Types'
import { useButton } from '@react-aria/button'
import { mergeProps } from '@react-aria/utils'
import { useRetryMessage } from 'hooks/queries/chat/useRetryMessage'
import { ArrowRefresh, Download, ThumbDown, ThumbUp } from 'blaze-ui/icons'
import { useRateMessage } from 'hooks/queries/chat/useRateMessage'
import {
  ChatDesignPreviewContent,
  ChatGeneratedImageContent,
  ChatGeneratedMediaContent,
  ChatGeneratedVideoContent,
  ChatMessage,
  ChatRatingContent,
} from 'api/clients/ChatClient'
import { fetchFile } from 'utils/fileDownloadUtils'
import { downloadFile } from 'components/VisualStudioPlayground/VisualEditor/utils/export/downloadFile'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ReplaceButton, ReplaceableContent } from './ReplaceButton'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useObjectRef } from 'utils/useObjectRef'
import { useOptionalChatComparison } from 'screens/Calendar/FullScreenPreview/ChatComparisonContext'

interface ActionIconButtonProps {
  onPress: () => void
  icon: React.ReactNode
  isDisabled: boolean
  'aria-label'?: string
}

const ActionIconButton = forwardRef<HTMLDivElement, ActionIconButtonProps>(
  ({ onPress, icon, isDisabled, ...rest }, fRef) => {
    const ref = useObjectRef<HTMLDivElement>(fRef)
    const { buttonProps } = useButton({ onPress, elementType: 'div', isDisabled }, ref)
    return (
      <div
        {...mergeProps(buttonProps, rest)}
        ref={ref}
        className={cx(Styles.actionIconButton, { [Styles.disabled]: isDisabled })}
      >
        {icon}
      </div>
    )
  },
)

const getSingleDownloadableContent = (message: ChatMessage): ChatGeneratedMediaContent | null => {
  const media = message.content.filter(
    (c): c is ChatGeneratedImageContent | ChatGeneratedVideoContent =>
      c.type === 'generated_image' || c.type === 'generated_video',
  )
  return media.length === 1 ? media[0] : null
}

const getReplaceableContent = (message: ChatMessage): ReplaceableContent | null => {
  const designContent = message.content.find((c): c is ChatDesignPreviewContent => c.type === 'design_content')
  if (designContent) {
    return { type: 'design', content: designContent }
  }

  const mediaContent = getSingleDownloadableContent(message)
  if (mediaContent) {
    return { type: 'media', content: mediaContent }
  }

  return null
}

export const LastMessageActionsV2 = ({ message }: LastMessageActionsProps) => {
  const { mutate: retryMessage, isLoading: isRetryLoading } = useRetryMessage()
  const { mutate: rateMessage, isLoading: isRateLoading } = useRateMessage()
  const [isDownloading, setIsDownloading] = useState(false)
  const comparisonContext = useOptionalChatComparison()

  const isComparisonMsg = !!comparisonContext?.isComparisonMessage(message.id)
  const comparisonPayload = comparisonContext?.getComparisonForMessage(message.id)

  const ratingMessage = message.content.find((content): content is ChatRatingContent => content.type === 'rated')
  const rating = comparisonPayload ? comparisonPayload.rating ?? ratingMessage?.rating : ratingMessage?.rating
  const singleContent = getSingleDownloadableContent(message)
  const replaceable = getReplaceableContent(message)

  const handleRetry = () => {
    retryMessage({ threadId: message.thread_id, id: message.id })
  }

  const handleRate = (rate: 'upvote' | 'downvote') => {
    if (isComparisonMsg && comparisonContext) {
      comparisonContext.rateComparison(message.id, rate)
    } else {
      rateMessage({ threadId: message.thread_id, messageId: message.id, rating: rate })
    }
  }

  const handleDownload = async () => {
    if (!singleContent) return

    setIsDownloading(true)
    const dismissToast = showToastMessage('Downloading...', 'loading')

    try {
      const url = singleContent.type === 'generated_image' ? singleContent.image_url : singleContent.video_url
      if (!url) return
      const file = await fetchFile({ url })
      downloadFile(file)
      showToastMessage('Download complete!')
    } catch {
      showToastMessage('Unable to download', 'error')
    } finally {
      setIsDownloading(false)
      dismissToast()
    }
  }

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.actions}>
        {singleContent && (
          <Tooltip label="Download">
            <ActionIconButton
              aria-label="Download"
              onPress={handleDownload}
              icon={<Download size={16} color="var(--dark-60)" />}
              isDisabled={isDownloading}
            />
          </Tooltip>
        )}
        <Tooltip label="Upvote">
          <ActionIconButton
            aria-label="Upvote"
            onPress={() => handleRate('upvote')}
            icon={<ThumbUp size={16} color={rating === 'upvote' ? 'var(--dark-100)' : 'var(--dark-60)'} />}
            isDisabled={isRateLoading}
          />
        </Tooltip>
        <Tooltip label="Downvote">
          <ActionIconButton
            aria-label="Downvote"
            onPress={() => handleRate('downvote')}
            icon={<ThumbDown size={16} color={rating === 'downvote' ? 'var(--dark-100)' : 'var(--dark-60)'} />}
            isDisabled={isRateLoading}
          />
        </Tooltip>
        <Tooltip label="Retry">
          <ActionIconButton
            aria-label="Retry"
            onPress={handleRetry}
            icon={<ArrowRefresh size={16} color="var(--dark-60)" />}
            isDisabled={isRetryLoading}
          />
        </Tooltip>
      </div>
      {replaceable && <ReplaceButton replaceable={replaceable} threadId={message.thread_id} messageId={message.id} />}
    </div>
  )
}
